"use client";

import { useId, useState } from "react";
import { ChipSelect } from "@/components/ui/chip-select";
import { Field, FieldDescription, FieldError, FieldLabel } from "@/components/ui/field";
import { InlineCombobox } from "@/components/ui/inline-combobox";
import { Input } from "@/components/ui/input";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { cn } from "@/lib/utils";
import { getConditionsForProductType } from "@shared/generated/productConditions";
import { isSizeIndexed, standardSizeOptions } from "@shared/price-index/size-key";
import {
  SIDE_OPTIONS,
  centsToInput,
  parsePriceCents,
  priceInputError,
  type CheckSide,
} from "./price-check-model";
import { PriceCheckVerdict, type TradeHrefs } from "./price-check-verdict";
import { PriceReportForm, type PriceReport, type PriceReportSource } from "./price-report-form";
import { PriceRuler } from "./price-ruler";
import {
  usePriceCheck,
  type PriceCheckDefaults,
  type PriceCheckInputs,
  type PriceCheckState,
} from "./use-price-check";

export type PriceCheckProduct = { slug: string; name: string };

type Option = { value: string; label: string };

const ANY = "";

function gradeOptions(productTypeSlug: string): Option[] {
  return [
    { value: ANY, label: "All grades" },
    ...getConditionsForProductType(productTypeSlug).map((grade) => ({
      value: grade.value,
      label: grade.label,
    })),
  ];
}

function sizeOptions(productTypeSlug: string): Option[] {
  const sizes = standardSizeOptions(productTypeSlug);
  if (sizes.length === 0) return [];
  return [{ value: ANY, label: "Any size" }, ...sizes.map((size) => ({ value: size.key, label: size.label }))];
}

function tradeHrefs(productTypeSlug: string, placeSlug: string | undefined): TradeHrefs {
  return {
    buyHref: placeSlug ? `/buy-${productTypeSlug}/${placeSlug}` : `/buy-${productTypeSlug}`,
    sellHref: `/sell-${productTypeSlug}`,
  };
}

function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Option[];
  onChange: (value: string) => void;
}) {
  const id = useId();
  return (
    <Field className="min-w-0">
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <InlineCombobox
        id={id}
        ariaLabel={label}
        value={value}
        options={options}
        placeholder={options[0]?.label ?? label}
        searchPlaceholder={`Search ${label.toLowerCase()}`}
        onSelect={onChange}
      />
    </Field>
  );
}

function PostalCodeField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const id = useId();
  return (
    <Field className="min-w-0">
      <FieldLabel htmlFor={id}>ZIP code</FieldLabel>
      <Input
        id={id}
        name="postal-code"
        autoComplete="postal-code"
        inputMode="text"
        maxLength={10}
        placeholder="Whole US"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </Field>
  );
}

function PriceField({
  value,
  label,
  onChange,
}: {
  value: string;
  label: string;
  onChange: (value: string) => void;
}) {
  const id = useId();
  const [touched, setTouched] = useState(false);
  return (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Input
        id={id}
        name="price"
        type="text"
        inputMode="decimal"
        variant="mono"
        placeholder="0.00"
        value={value}
        onChange={(event) => {
          setTouched(false);
          onChange(event.target.value);
        }}
        onBlur={() => setTouched(true)}
      />
      <FieldDescription>Per unit, picked up, before freight.</FieldDescription>
      <FieldError>{touched ? priceInputError(value) : null}</FieldError>
    </Field>
  );
}

function CheckFields({ check, products }: { check: PriceCheckState; products: PriceCheckProduct[] }) {
  const { inputs, update } = check;
  const sizes = sizeOptions(inputs.productTypeSlug);
  return (
    <div className="grid gap-4">
      {products.length > 1 && (
        <ChipSelect
          aria-label="Material"
          value={inputs.productTypeSlug}
          options={products.map((product) => ({ value: product.slug, label: product.name }))}
          onValueChange={(productTypeSlug) =>
            update({ productTypeSlug, condition: ANY, sizeKey: ANY, priceCents: null })
          }
        />
      )}
      <SegmentedControl<CheckSide>
        aria-label="Buying or selling"
        value={inputs.side}
        options={SIDE_OPTIONS}
        onValueChange={(side) => update({ side })}
      />
      <div className={cn("grid gap-3", sizes.length > 0 ? "sm:grid-cols-3" : "sm:grid-cols-2")}>
        <PostalCodeField value={inputs.postalCode} onChange={(postalCode) => update({ postalCode })} />
        <SelectField
          label="Grade"
          value={inputs.condition}
          options={gradeOptions(inputs.productTypeSlug)}
          onChange={(condition) => update({ condition })}
        />
        {sizes.length > 0 && (
          <SelectField
            label="Size"
            value={inputs.sizeKey}
            options={sizes}
            onChange={(sizeKey) => update({ sizeKey })}
          />
        )}
      </div>
    </div>
  );
}

function PriceInput({ check }: { check: PriceCheckState }) {
  const { inputs, band, update } = check;
  const [priceInput, setPriceInput] = useState(
    inputs.priceCents === null ? "" : centsToInput(inputs.priceCents),
  );
  const [shownCents, setShownCents] = useState(inputs.priceCents);
  if (shownCents !== inputs.priceCents) {
    setShownCents(inputs.priceCents);
    if (parsePriceCents(priceInput) !== inputs.priceCents) {
      setPriceInput(inputs.priceCents === null ? "" : centsToInput(inputs.priceCents));
    }
  }
  const label = inputs.side === "buy" ? "Price you pay" : "Price you're offered";
  return (
    <div className="grid gap-3">
      <PriceField
        label={label}
        value={priceInput}
        onChange={(value) => {
          setPriceInput(value);
          update({ priceCents: parsePriceCents(value) });
        }}
      />
      {band && (
        <PriceRuler
          band={band}
          priceCents={inputs.priceCents}
          label={label}
          onChange={(priceCents) => update({ priceCents })}
        />
      )}
    </div>
  );
}

function isMissingSize(inputs: PriceCheckInputs): boolean {
  return isSizeIndexed(inputs.productTypeSlug) && !inputs.sizeKey;
}

function reportFor(inputs: PriceCheckInputs, place: PriceCheckState["place"]): PriceReport | null {
  if (!inputs.condition || isMissingSize(inputs) || inputs.priceCents === null) return null;
  return {
    productTypeSlug: inputs.productTypeSlug,
    condition: inputs.condition,
    sizeKey: inputs.sizeKey,
    priceCents: inputs.priceCents,
    side: inputs.side,
    postalCode: inputs.postalCode,
    stateCode: place?.code ?? null,
  };
}

function ReportPrompt({
  inputs,
  report,
  source,
}: {
  inputs: PriceCheckInputs;
  report: PriceReport | null;
  source: PriceReportSource;
}) {
  if (inputs.priceCents === null) return null;
  const missing = isMissingSize(inputs) ? (inputs.condition ? "size" : "grade and size") : "grade";
  return (
    <div className="border-t border-border-subtle pt-4">
      {report ? (
        <PriceReportForm report={report} source={source} />
      ) : (
        <p className="text-sm text-fg-muted">Pick a {missing} to add your price to the index.</p>
      )}
    </div>
  );
}

function ToolHeading({ id, heading }: { id: string; heading: string }) {
  return (
    <h2 id={id} className="text-xl font-semibold text-foreground">
      {heading}
    </h2>
  );
}

export function PriceCheckTool({
  defaults,
  products = [],
  source,
  heading = "Check a price",
  compact = false,
}: {
  defaults: PriceCheckDefaults;
  products?: PriceCheckProduct[];
  source: PriceReportSource;
  heading?: string | null;
  compact?: boolean;
}) {
  const headingId = useId();
  const check = usePriceCheck(defaults);
  const { inputs, band, place } = check;
  return (
    <section
      aria-labelledby={heading ? headingId : undefined}
      aria-label={heading ? undefined : "Price check"}
      className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]"
    >
      <div className="grid content-start gap-5">
        {heading && <ToolHeading id={headingId} heading={heading} />}
        <CheckFields check={check} products={products} />
        <PriceInput key={inputs.productTypeSlug} check={check} />
      </div>
      <aside className="grid content-start gap-5 rounded-lg bg-surface-2 p-5">
        <PriceCheckVerdict
          band={band}
          priceCents={inputs.priceCents}
          side={inputs.side}
          isLoading={check.isLoading}
          hrefs={tradeHrefs(inputs.productTypeSlug, place?.slug)}
          productTypeSlug={inputs.productTypeSlug}
          showImpact={!compact}
        />
        <ReportPrompt inputs={inputs} report={reportFor(inputs, place)} source={source} />
      </aside>
    </section>
  );
}
